"use client"
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiOutlineGlobe, HiTranslate } from 'react-icons/hi';

const LanguageSwitcher = ({ locale, toggleLanguage }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [ripple, setRipple] = useState({ x: 0, y: 0, isActive: false });
  

  const handleClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setRipple({ x, y, isActive: true });
    setTimeout(() => setRipple({ ...ripple, isActive: false }), 600);
    toggleLanguage();
  };

  return (
    <motion.div 
      className="relative"
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <motion.button
        onClick={handleClick}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className={`
          relative flex items-center justify-center gap-3 
          bg-gradient-to-br from-[var(--color-beige-light)]/80 to-[var(--color-beige-light)]/60
          hover:from-[var(--color-olive-dark)] hover:to-[var(--color-olive-dark)]/80
          border-2 border-[var(--color-olive-dark)]/20
          hover:border-[var(--color-gold-bright)]/70
          text-[var(--color-white)]
          hover:text-[var(--color-beige-light)]
          font-semibold px-6 py-1 rounded-2xl
          transition-all duration-500
          shadow-lg hover:shadow-2xl
          backdrop-blur-md
          min-w-[120px]
          group
          overflow-hidden
        `}
        whileHover={{ 
          scale: 1.05, 
          y: -2,
          transition: { type: "spring", stiffness: 400, damping: 17 }
        }}
        whileTap={{ 
          scale: 0.95,
          transition: { type: "spring", stiffness: 400, damping: 17 }
        }}
        style={{
          boxShadow: isFocused ? '0 0 0 3px rgba(139, 167, 136, 0.4)' : '',
        }}
      >
        {/* Ripple Effect */}
        {ripple.isActive && (
          <motion.span
            className="absolute bg-[var(--color-gold-bright)]/30 rounded-full"
            initial={{ scale: 0, opacity: 0.7 }}
            animate={{ scale: 2.5, opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            style={{
              left: ripple.x,
              top: ripple.y,
              width: '20px',
              height: '20px',
            }}
          />
        )}

        {/* Animated Globe Icon */}
        <motion.div
          animate={{ 
            rotate: isHovered ? [0, -10, 10, 0] : 0,
            scale: isHovered ? 1.2 : 1
          }}
          transition={{ 
            rotate: { duration: 0.5, ease: "easeInOut" },
            scale: { duration: 0.3 }
          }}
          className="relative z-10"
        >
          <HiOutlineGlobe className="text-xl" />
        </motion.div>
        
        {/* Language Text with Smooth Transition */}
        <div className="relative overflow-hidden h-6 flex items-center">
          <AnimatePresence mode="wait">
            <motion.span
              key={locale}
              initial={{ y: 30, opacity: 0, rotateX: 90 }}
              animate={{ y: 0, opacity: 1, rotateX: 0 }}
              exit={{ y: -30, opacity: 0, rotateX: -90 }}
              transition={{ duration: 0.4, ease: "backOut" }}
              className="block font-medium"
            >
              {locale === 'ar' ? 'EN' : 'AR'}
            </motion.span>
          </AnimatePresence>
        </div>

        {/* Subtle Background Pattern */}
        <motion.div 
          className="absolute inset-0 opacity-10"
          animate={{ 
            backgroundPosition: isHovered ? '100% 100%' : '0% 0%'
          }}
          transition={{ duration: 8, ease: "linear", repeat: Infinity }}
          style={{
            backgroundImage: `radial-gradient(circle, var(--color-olive-dark) 1px, transparent 1px)`,
            backgroundSize: '20px 20px'
          }}
        />

        {/* Shine Effect on Hover */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-[var(--color-gold-bright)]/30 to-transparent"
          initial={{ x: "-100%", opacity: 0 }}
          animate={{ 
            x: isHovered ? "100%" : "-100%", 
            opacity: isHovered ? 1 : 0 
          }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
        />

        {/* Border Glow Effect */}
        <motion.div
          className="absolute inset-0 rounded-2xl"
          initial={{ boxShadow: 'inset 0 0 0 0px rgba(210, 180, 140, 0)' }}
          animate={{ 
            boxShadow: isHovered 
              ? 'inset 0 0 20px 2px rgba(210, 180, 140, 0.4)' 
              : 'inset 0 0 0 0px rgba(210, 180, 140, 0)' 
          }}
          transition={{ duration: 0.4 }}
        />
      </motion.button>

      {/* Floating Label with Arrow */}
      <motion.div
        initial={{ opacity: 0, y: 10, scale: 0.9 }}
        animate={{ 
          opacity: isHovered ? 1 : 0, 
          y: isHovered ? 0 : 10,
          scale: isHovered ? 1 : 0.9
        }}
        transition={{ duration: 0.3, type: "spring", stiffness: 200 }}
        className="absolute !z-50 -bottom-9 left-1/2 transform -translate-x-1/2 whitespace-nowrap text-sm font-medium text-[var(--color-olive-dark)] bg-[var(--color-beige-light)]/90 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-lg border border-[var(--color-olive-dark)]/10"
      >
        <div className="flex items-center gap-1.5">
          <HiTranslate className="text-[var(--color-olive-dark)]/70" />
          <span>{locale === 'ar' ? 'Switch to English' : 'التغيير إلى العربية'}</span>
        </div>
        
        {/* Arrow pointing to button */}
        <motion.div
          className="absolute -top-1.5 left-1/2 transform -translate-x-1/2 w-3 h-3 bg-[var(--color-beige-light)]/90 rotate-45 border-l border-t border-[var(--color-olive-dark)]/10"
          initial={{ opacity: 0 }}
          animate={{ opacity: isHovered ? 1 : 0 }}
          transition={{ delay: 0.1 }}
        />
      </motion.div>
    </motion.div>
  );
};

export default LanguageSwitcher;